import { useTranslation as useTranslationBase } from "react-i18next";
import { useEffect, useState } from "react";

const isProduction = process.env.REACT_APP_TARGET_ENV === "production";

/**
 * Custom hook that ensures proper translation updates when language changes
 * @param namespace - The translation namespace ('common', 'quote', 'procedures')
 */
export function useTranslationNamespace(
  namespace: "common" | "quote" | "procedures" | "campaignAction" | "patientPortal",
) {
  // @ts-ignore - useTranslation with namespace parameter
  const { t, i18n } = useTranslationBase(namespace);
  const [, setLanguageChanged] = useState(0);

  useEffect(() => {
    if (!isProduction) {
      console.log(
        `[useTranslationNamespace:${namespace}] Hook mounted, current language:`,
        i18n.language,
      );
    }

    // Force component to re-render when language changes
    const handleLanguageChanged = (lng: string) => {
      if (!isProduction) {
        console.log(
          `[useTranslationNamespace:${namespace}] Language changed event fired, new language:`,
          lng,
        );
      }
      setLanguageChanged((prev) => {
        if (!isProduction) {
          console.log(
            `[useTranslationNamespace:${namespace}] Forcing re-render, state updated`,
          );
        }
        return prev + 1;
      });
    };

    i18n.on("languageChanged", handleLanguageChanged);
    if (!isProduction) {
      console.log(
        `[useTranslationNamespace:${namespace}] Event listener attached`,
      );
    }

    return () => {
      i18n.off("languageChanged", handleLanguageChanged);
      if (!isProduction) {
        console.log(
          `[useTranslationNamespace:${namespace}] Event listener removed`,
        );
      }
    };
  }, [i18n, namespace]);

  // Log values for debugging
  if (!isProduction) {
    console.log(
      `[useTranslationNamespace:${namespace}] Returning translations, current language: ${i18n.language}, sample key: contact.heading = "${t("contact.heading")}"`,
    );
  }

  return { t, i18n };
}

export default useTranslationNamespace;
